/**
 * Карта доступа для главной v2 (спека docs/superpowers/specs/2026-09-06-home-v2-access-map-design.md).
 *
 * Чистая функция: из ответа /plans/v2/roadmaps/ и trialInfo собирает, что
 * человеку открыто, что нет, и что делать сейчас. Никаких запросов, никакого DOM.
 */

export const ANNUAL_PRICE_LABEL = '28 900 ₽ в год';

const TITLE_CASE = (s) => {
    const t = String(s || '').trim();
    if (!t) return '';
    // «БИОХИМИЯ» → «Биохимия», «ГАСТРОИНТЕСТИНАЛЬНАЯ СИСТЕМА (GI)» → «Гастроинтестинальная система (GI)»
    const lower = t.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1).replace(/\(([a-z]+)\)/g, (m, g) => `(${g.toUpperCase()})`);
};

function disciplineProgress(m) {
    // «Начата» = есть хотя бы один пройденный день. Свободный порядок ставит
    // current_day_number=1 всем открытым модулям, так что сам по себе он не признак.
    if (m.status === 'completed') return 'пройдена';
    if (m.completed_days > 0) return `день ${m.current_day_number || m.completed_days + 1} из ${m.total_days}`;
    return 'не начата';
}

function buildToday(roadmap, disciplines) {
    if (!roadmap || !Array.isArray(roadmap.modules) || roadmap.modules.length === 0) return { kind: 'none' };
    if (roadmap.is_enrolled && roadmap.current_module_id) {
        const cur = roadmap.modules.find(m => m.id === roadmap.current_module_id);
        if (cur && cur.status !== 'completed') {
            return {
                kind: 'continue', moduleId: cur.id, roadmapId: roadmap.id,
                title: TITLE_CASE(cur.title), day: cur.current_day_number || 1, total: cur.total_days,
            };
        }
    }
    // Любая незавершённая открытая дисциплина с прогрессом → продолжить её
    const inProgress = roadmap.modules.find(m => !m.trial_locked && m.current_day_number && m.status !== 'completed');
    if (roadmap.is_enrolled && inProgress) {
        return {
            kind: 'continue', moduleId: inProgress.id, roadmapId: roadmap.id,
            title: TITLE_CASE(inProgress.title), day: inProgress.current_day_number, total: inProgress.total_days,
        };
    }
    const firstOpen = roadmap.modules.find(m => !m.trial_locked && m.status !== 'completed');
    if (firstOpen) return { kind: 'start', moduleId: firstOpen.id, roadmapId: roadmap.id, title: TITLE_CASE(firstOpen.title) };
    return { kind: 'none' };
}

export function buildAccessMap({ roadmap, trialInfo, user }) {
    // Полный доступ: оплачено. Админ видит карту как оплативший.
    const full = trialInfo?.state === 'paid' || user?.role === 'admin';
    const modules = roadmap && Array.isArray(roadmap.modules) ? roadmap.modules : [];

    const disciplines = modules.map(m => ({
        id: m.id,
        roadmapId: roadmap.id,
        title: TITLE_CASE(m.title),
        locked: m.trial_locked === true,
        progress: m.trial_locked ? 'в APEX PRO' : disciplineProgress(m),
        status: m.status,
    }));
    const openCount = disciplines.filter(d => !d.locked).length;
    const total = disciplines.length;

    const open = (name, note = '') => ({ name, state: 'open', note });
    const partial = (name, note) => ({ name, state: 'partial', note });
    const locked = (name) => ({ name, state: 'locked', note: 'в APEX PRO' });

    const tools = full ? [
        open('Мини-тесты'), open('Клинические задачи'), open('Учебные планы'), open('Режим экзамена'),
        open('Видеолекции'), open('Anki'), open('PDF презентаций'),
    ] : [
        open('Мини-тесты', '10 в день'),
        partial('Клинические задачи', 'в открытых дисциплинах'),
        partial('Учебные планы', total ? `${openCount} из ${total} дисциплин` : 'открытые дисциплины'),
        open('Режим экзамена'),
        locked('Видеолекции'),
        locked('Anki'),
        locked('PDF презентаций'),
    ];

    return {
        full,
        trialDaysLeft: trialInfo?.state === 'active_trial' ? (trialInfo.days_left ?? 0) : null,
        today: buildToday(roadmap, disciplines),
        disciplines,
        tools,
        cta: full ? null : { label: `Открыть всё · ${ANNUAL_PRICE_LABEL}` },
    };
}
